import React from 'react';
import { useNavigate } from 'react-router-dom';
import SharedFilters from '../../../components/SharedFilters';
import AppActionMenu from '../../../components/AppActionMenu';
import AppPagination from '../../../components/AppPagination';
import InventorySubNav from './components/InventorySubNav';
import { useInventoryListScreen } from './hooks/useInventoryListScreen';
import { appToast } from '../../../components/AppToast';

export default function InventoryListScreen() {
  const { state, actions } = useInventoryListScreen();
  const navigate = useNavigate();

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* Header */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-outline-variant/10 pb-5">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">Inventory Master</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            Inventory Registry
          </h1>
          <p className="mt-2 text-sm text-on-surface-variant max-w-xl leading-relaxed">
            Manage product catalogs, raw materials, service assets, barcodes, and current stock status.
          </p>
        </div>
        <InventorySubNav />
      </section>

      {/* Shared Filters Component */}
      <SharedFilters
        searchField={state.searchField}
        searchText={state.searchText}
        statusFilter={state.statusFilter}
        companyFilter={state.companyFilter}
        branchFilter={state.branchFilter}
        departmentFilter={state.departmentFilter}
        placeFilter={state.placeFilter}
        groupFilter={state.groupFilter}
        typeFilter={state.typeFilter}
        valClassFilter={state.valClassFilter}
        stockTypeFilter={state.stockTypeFilter}
        filterFilter={state.filterFilter}
        deptStockFilter={state.deptStockFilter}
        fromDate={state.fromDate}
        toDate={state.toDate}

        setSearchField={actions.setSearchField}
        setSearchText={actions.setSearchText}
        setStatusFilter={actions.setStatusFilter}
        setCompanyFilter={actions.setCompanyFilter}
        setBranchFilter={actions.setBranchFilter}
        setDepartmentFilter={actions.setDepartmentFilter}
        setPlaceFilter={actions.setPlaceFilter}
        setGroupFilter={actions.setGroupFilter}
        setTypeFilter={actions.setTypeFilter}
        setValClassFilter={actions.setValClassFilter}
        setStockTypeFilter={actions.setStockTypeFilter}
        setFilterFilter={actions.setFilterFilter}
        setDeptStockFilter={actions.setDeptStockFilter}
        setFromDate={actions.setFromDate}
        setToDate={actions.setToDate}
        onReset={actions.resetFilters}

        searchFieldOptions={[{ value: 'Name', label: 'Item Name' }]}
        statusOptions={[{ value: 'Active', label: 'Active' }, { value: 'Archive', label: 'Archived' }, { value: 'All', label: 'All' }]}
        companyOptions={[{ value: 'All', label: 'All Companies' }]}
        branchOptions={[{ value: 'All', label: 'All Branches' }]}
        departmentOptions={[{ value: 'All', label: 'All Departments' }]}
        placeOptions={[{ value: 'All', label: 'All Places' }]}
        groupOptions={[{ value: 'All', label: 'All Groups' }]}
        typeOptions={[{ value: 'All', label: 'All Types' }]}
        valClassOptions={[{ value: 'All', label: 'All Val Classes' }]}
        stockTypeOptions={[{ value: 'As per book', label: 'As per book' }]}
        filterOptions={[{ value: 'Show unused items', label: 'Show unused items' }]}
        
        visibleAccordionFilters={['company', 'branch', 'department', 'place', 'group', 'type', 'valClass', 'stockType', 'filter', 'fromDate', 'toDate', 'deptStock']}
        
        tools={[
          { key: 'upload-items', label: 'Upload Items', icon: 'upload' },
          { key: 'upload-price', label: 'Upload Price List', icon: 'upload' },
          { key: 'download-items', label: 'Download Items', icon: 'download' },
          { key: 'download-price', label: 'Download Price List', icon: 'download' },
          { key: 'print', label: 'Print', icon: 'print' },
          { key: 'update-currency', label: 'Update Currency', icon: 'autorenew' },
          { key: 'item-search', label: 'Item Search', icon: 'search' },
          { key: 'update-columns', label: 'Update Columns', icon: 'settings' },
          { key: 'update-rol', label: 'Update ROL Qty', icon: 'edit' },
          { key: 'expiry-report', label: 'Expiry Report', icon: 'analytics' }
        ]}
        onToolSelect={(key) => {
          if (key === 'download-items' || key === 'download') {
            actions.triggerExport({
              viewMode: state.statusFilter,
              searchText: state.searchText,
              groupId: state.groupFilter !== 'All' ? state.groupFilter : undefined,
            });
          }
        }}
      />


      {/* Data Table Card */}
      <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest overflow-hidden shadow-sm flex flex-col">
        
        {/* Header Row */}
        <div className="grid grid-cols-12 px-8 py-3.5 border-b border-outline-variant/25 bg-surface-container-low/60 text-xs font-bold uppercase tracking-[0.08em] text-on-surface-variant select-none">
          <div className="col-span-5">Item Details</div>
          <div className="col-span-2">UPC</div>
          <div className="col-span-2">HSN/SAC</div>
          <div className="col-span-1 text-right">Qty</div>
          <div className="col-span-1 text-center">Group</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>

        {/* Data Rows */}
        <div className="flex flex-col">
          {state.isLoading ? (
            <div className="px-6 py-16 text-center text-sm text-on-surface-variant font-medium">
              Loading inventory catalog...
            </div>
          ) : state.rows.length === 0 ? (
            <div className="px-6 py-16 text-center text-sm text-on-surface-variant font-medium">
              No inventory items found.
            </div>
          ) : (
            state.rows.map((row) => (
              <div 
                key={row.id} 
                className="grid grid-cols-12 items-center px-8 py-4 border-b border-outline-variant/10 hover:bg-surface-container-low/40 transition-colors last:border-b-0"
              >
                {/* Item Details */}
                <div className="col-span-5 flex items-center gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#dae2ff]/40 text-primary">
                    <span className="material-symbols-outlined">inventory</span>
                  </div>
                  <div>
                    <p className="text-base font-bold text-on-surface select-text">{row.name}</p>
                    <p className="text-xs text-on-surface-variant font-medium select-text">ID: {row.code}</p>
                  </div>
                </div>

                {/* UPC */}
                <div className="col-span-2 text-xs font-semibold text-on-surface-variant select-text">
                  {row.upc || '—'}
                </div>

                {/* HSN/SAC */}
                <div className="col-span-2 text-xs font-semibold text-on-surface-variant select-text">
                  {row.hsnSac || '—'}
                </div>

                {/* Qty with Positive/Negative style and Swapped colors */}
                <div className="col-span-1 text-right">
                  <span className={`font-bold text-sm tracking-tight tabular-nums ${
                    row.qty < 0 ? 'text-amber-600' : row.qty > 0 ? 'text-emerald-600' : 'text-on-surface-variant'
                  }`}>
                    {row.qty.toFixed(2)}
                  </span>
                </div>

                {/* Group */}
                <div className="col-span-1 text-center select-none">
                  {row.groupName ? (
                    <span className={`inline-flex items-center rounded-lg px-2.5 py-1 text-xs font-semibold ${
                      row.groupName === 'Products' 
                        ? 'bg-blue-50 text-blue-700' 
                        : row.groupName === 'Services' 
                        ? 'bg-purple-50 text-purple-700' 
                        : 'bg-orange-50 text-orange-700'
                    }`}>
                      {row.groupName}
                    </span>
                  ) : (
                    <span className="text-xs text-on-surface-variant/55">—</span>
                  )}
                </div>

                {/* Actions */}
                <div className="col-span-1 flex justify-end select-none">
                  <AppActionMenu
                    triggerLabel=""
                    triggerIcon="more_vert"
                    showChevron={false}
                    triggerClassName="rounded-lg p-1.5 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent"
                    items={[
                      { key: 'edit', label: 'Edit', icon: 'edit' },
                      { key: 'archive', label: row.status === 'Active' ? 'Archive' : 'Unarchive', icon: row.status === 'Active' ? 'archive' : 'unarchive' },
                      { key: 'delete', label: 'Delete', icon: 'delete' },
                      { key: 'copy', label: 'Copy', icon: 'content_copy' },
                      { key: 'branchwise-stock', label: 'Branchwise Stock', icon: 'location_on' },
                      { key: 'analytics', label: 'Analytics', icon: 'trending_up' },
                      { key: 'image-list', label: 'Image List', icon: 'image' },
                      { key: 'print-barcode', label: 'Print Barcode', icon: 'barcode' },
                      { key: 'sales-order', label: 'Sales Order', icon: 'description' },
                      { key: 'purchase-order', label: 'Purchase Order', icon: 'description' },
                      { key: 'delivery-challan', label: 'Delivery Challan', icon: 'description' },
                      { key: 'proforma-invoice', label: 'Proforma Invoice', icon: 'description' },
                      { key: 'grn', label: 'GRN', icon: 'description' },
                    ]}
                    onSelect={(key) => {
                      if (key === 'edit') {
                        appToast.info('Edit option is currently disabled.');
                      } else if (key === 'archive') {
                        actions.handleArchive(row.id);
                      } else if (key === 'delete') {
                        actions.handleDelete(row.id);
                      } else if (key === 'copy') {
                        appToast.info(`Copying catalog details for "${row.name}"...`);
                      } else if (key === 'branchwise-stock') {
                        appToast.info(`Opening Branchwise Stock report for "${row.name}"...`);
                      } else if (key === 'analytics') {
                        appToast.info(`Opening Stock Analytics for "${row.name}"...`);
                      } else if (key === 'image-list') {
                        appToast.info(`Opening Image Gallery for "${row.name}"...`);
                      } else if (key === 'print-barcode') {
                        appToast.info(`Printing barcode for "${row.name}"...`);
                      } else if (key === 'sales-order') {
                        appToast.info(`Opening Sales Orders report for "${row.name}"...`);
                      } else if (key === 'purchase-order') {
                        appToast.info(`Opening Purchase Orders report for "${row.name}"...`);
                      } else if (key === 'delivery-challan') {
                        appToast.info(`Opening Delivery Challan report for "${row.name}"...`);
                      } else if (key === 'proforma-invoice') {
                        appToast.info(`Opening Proforma Invoices report for "${row.name}"...`);
                      } else if (key === 'grn') {
                        appToast.info(`Opening GRN report for "${row.name}"...`);
                      }
                    }}
                  />
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer Control */}
        <div className="flex items-center justify-between bg-surface-container px-6 py-4 text-sm text-on-surface-variant font-semibold select-none border-t border-outline-variant/10">
          <span>
            Showing {((state.page - 1) * state.pageSize) + 1} to {Math.min(state.page * state.pageSize, state.meta.totalCount)} of {state.meta.totalCount} entries
          </span>
          
          <AppPagination
            page={state.page}
            totalPages={Math.ceil(state.meta.totalCount / state.pageSize) || 1}
            onPageChange={actions.setPage}
            onPrevious={() => actions.setPage(p => Math.max(p - 1, 1))}
            onNext={() => actions.setPage(p => Math.min(p + 1, Math.ceil(state.meta.totalCount / state.pageSize)))}
            canPrevious={state.page > 1}
            canNext={state.page < Math.ceil(state.meta.totalCount / state.pageSize)}
          />
        </div>

      </div>
    </div>
  );
}
